import React, { useEffect, useMemo, useState } from 'react';
import Searching from './searching';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ChevronUp, X } from 'lucide-react';
import { cn, formatNumber } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { exportToCsv } from '@/lib/utils/downloadXlsx';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import LocalImage from '@/components/Common/LocalImage';
import Image from 'next/image';
import { postExportFilderRecord } from '@/lib/service/dashboard';
import { ga_logEvent } from '@/shared/ga';
import { EventName } from '@/shared/event-types';
import Link from 'next/link';
import { getBusinessHost } from '@/shared/app-common';
import { useRouter } from 'next/router';

const ErrorIcon = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="8" r="8" fill="#FF0C59" />
    <rect x="7" y="3" width="2" height="7" rx="1" fill="white" />
    <rect x="7" y="11" width="2" height="2" rx="1" fill="white" />
  </svg>
);

// 表格数据类型
interface ProfileData {
  id: number;
  avatar: string;
  username: string;
  fullname: string;
  followers: string;
  following: string;
  posts: string;
  biography: string;
  category: string;
  email: string;
  emailColor?: string;
}

// 模拟数据

export default function ResultPeal({
  data,
  loading,
  animateLoading,
  currentTab,
  searchId,
}: {
  data: any;
  loading: boolean;
  animateLoading: boolean;
  currentTab: string;
  searchId: string;
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10); // 每页显示5条数据，便于展示分页效果
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [showBanner, setShowBanner] = useState(true);
  const { t } = useTranslation();
  const route = useRouter();
  const mockData = useMemo(() => {
    return data?.items || [];
  }, [data]);
  const BANNER_HIDE_KEY = 'ins_export_banner_hide_until';

  const businessHost = getBusinessHost();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hideUntil = window.localStorage.getItem(BANNER_HIDE_KEY);
    if (hideUntil && dayjs(hideUntil).isAfter(dayjs())) {
      setShowBanner(false);
    } else if (hideUntil) {
      window.localStorage.removeItem(BANNER_HIDE_KEY);
    }
  }, []);

  const handleCloseBanner = () => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(BANNER_HIDE_KEY, dayjs().add(1, 'day').toISOString());
    }
    setShowBanner(false);
  };

  // 处理排序
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // 对数据进行排序
  const sortedData = React.useMemo(() => {
    if (!sortColumn) return mockData;

    return [...mockData].sort((a, b) => {
      let aValue: number | string = 0;
      let bValue: number | string = 0;

      switch (sortColumn) {
        case 'followers':
          aValue = a.follower_count;
          bValue = b.follower_count;
          break;
        case 'following':
          aValue = a.following_count;
          bValue = b.following_count;
          break;
        case 'posts':
          aValue = a.media_count;
          bValue = b.media_count;
          break;
        case 'email':
          // 按照有无内容排序
          const hasEmailA = a.email && a.email.trim() !== '';
          const hasEmailB = b.email && b.email.trim() !== '';

          // 如果一个有email一个没有，优先排序
          if (hasEmailA !== hasEmailB) {
            if (sortDirection === 'asc') {
              return hasEmailA ? -1 : 1; // 有email的排前面
            } else {
              return hasEmailA ? 1 : -1; // 没有email的排前面
            }
          }

          // 如果都有或都没有，按字符串排序
          aValue = a.email || '';
          bValue = b.email || '';
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // 字符串排序
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      }
      return aValue < bValue ? 1 : -1;
    });
  }, [sortColumn, sortDirection, mockData]);

  // 计算总页数
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  // 计算当前页显示的数据
  const currentData = React.useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, currentPage, rowsPerPage]);

  const handleExport = (isProfile: boolean) => {
    const exportData = isProfile ? mockData : mockData.filter((item: any) => item.email);
    if (exportData.length === 0) {
      return;
    }

    const fileHeader = [
      'Instagram ID',
      'Username',
      'Full name',
      'Is Private',
      'Profile link',
      'Following count',
      'Follower Count',
      'Posts count',
      'Biography',
      'Public email',
      'Email verified',
    ];
    const fileName = `instagram-${currentTab}-${isProfile ? 'all' : 'emails'}-${dayjs().format('YYYY-MM-DD')}`;
    exportToCsv({
      filename: fileName,
      headers: fileHeader,
      data: exportData.map((item: any) => [
        item.pk_id,
        item.username,
        item.full_name,
        item.is_private ? 'Yes' : 'No',
        item.media_url,
        item.following_count,
        item.follower_count,
        item.media_count,
        item.profile,
        item.email,
        item.email_verified,
      ]),
    });
    postExportFilderRecord(searchId);
  };

  // 手动控制，显示加载动画
  if (animateLoading) {
    return <Searching className="bg-white" />;
  }

  return (
    <div className="max-w-[1220px] lg:mx-auto mx-2 lg:mt-16 mt-6 bg-white rounded-[24px] border border-[#E4E4E7] overflow-hidden shadow-[0px_4px_12px_0px_rgba(0,0,0,0.08)]">
      {showBanner && (
        <div className="top-0 left-0 right-0 px-[18px] py-3 flex items-center gap-3 bg-[#FFF8E7] text-[#914B2E] shadow-[0px_4px_12px_0px_rgba(255,248,231,0.1)]">
          <Image src="/images/instagram-email-finder/warn-icon.svg" width={32} height={32} alt="icon" />
          <div className="flex-1 flex max-lg:flex-col lg:items-center justify-between lg:gap-4 overflow-hidden">
            <style>{`
              @keyframes runMarquee {
                0% { transform: translateX(0); }
                100% { transform: translateX(-800px); }
              }
              @media (max-width: 1024px) {
                .animateRunMarquee {
                  animation: runMarquee 15s linear infinite;
                }
              }
            `}</style>
            <div className="flex-1 lg:w-full flex max-lg:items-center max-lg:w-[1600px] animateRunMarquee max-lg:whitespace-nowrap">
              <div className="lg:flex-1 max-lg:w-[800px] lg:w-0 lg:flex lg:flex-col lg:leading-tight max-lg:text-nowrap max-lg:shrink-0">
                <span className="lg:font-semibold mr-1">{t('Exports here are one-time only.')}</span>
                <span>{t('The business version includes export history and higher export limits.')}</span>
              </div>
              <div className="lg:hidden w-[800px] shrink-0 text-nowrap">
                <span className="lg:font-semibold mr-1">{t('Exports here are one-time only.')}</span>
                <span>{t('The business version includes export history and higher export limits.')}</span>
              </div>
            </div>
            <Link
              className="max-lg:self-start font-semibold hover:text-[#914B2E]/80 text-nowrap shrink-0"
              href={businessHost}
              target="_blank"
            >
              {t('Switch to Business')} →
            </Link>
          </div>
          <div className="pl-3 border-l border-[#914B2E]/30 max-lg:self-start max-lg:mt-1">
            <X size={18} className="cursor-pointer" onClick={handleCloseBanner} />
          </div>
        </div>
      )}
      <div className="px-2 lg:px-0">
        <div className="flex max-lg:flex-col items-center justify-between lg:px-8 px-4 py-10">
          {/* 标题和描述 */}
          <div className="max-lg:text-center ">
            <div className="text-[#0C0C0C] text-[32px] font-bold">{t('Results')}</div>
            <p className="text-[#0C0C0C] lg:text-[20px] text-[16px] leading-[40px] flex items-center justify-center gap-2">
              {loading && (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full size-4 border-b-2 border-[#725CFF]"></div>
                </div>
              )}
              <div>
                {route.locale === 'en' ? (
                  <>
                    Found <span className="text-[#725CFF]">{data.count ?? 0}</span> profiles, with{' '}
                    <span className="text-[#EC4899]">{data.email_count ?? 0}</span> emails available.
                  </>
                ) : (
                  t('Found {{count}} profiles, with {{emailCount}} emails available.', {
                    count: data.count ?? 0,
                    emailCount: data.email_count ?? 0,
                  })
                )}
              </div>
            </p>
          </div>

          {/* 导出按钮 */}
          <div className="flex lg:flex-row flex-col justify-center lg:gap-8 gap-4">
            <button
              className="flex items-center justify-center gap-2.5 px-4  py-[11px] bg-[#725CFF] rounded-md hover:bg-[#5a47cc] transition-colors"
              onClick={() => {
                ga_logEvent(EventName.export_profiles_click);
                handleExport(true);
              }}
            >
              <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M8.5 0.34375C4.27156 0.34375 0.84375 3.77156 0.84375 8C0.84375 12.2284 4.27156 15.6562 8.5 15.6562C12.7284 15.6562 16.1562 12.2284 16.1562 8C16.1562 3.77156 12.7284 0.34375 8.5 0.34375ZM11.5956 8.80234L8.87844 11.5197C8.86625 11.5353 8.85328 11.5505 8.83891 11.5647C8.74531 11.6583 8.62266 11.7045 8.5 11.7039C8.3775 11.7047 8.25469 11.6583 8.16109 11.5647C8.14672 11.5503 8.13359 11.5353 8.12156 11.5197L5.40438 8.8025C5.21875 8.61687 5.21875 8.31594 5.40438 8.13031C5.59 7.94469 5.89094 7.94469 6.07672 8.13031L8.02469 10.0781V4.77141C8.02469 4.50891 8.2375 4.29609 8.5 4.29609C8.7625 4.29609 8.97531 4.50891 8.97531 4.77141V10.0781L10.9234 8.13016C11.1091 7.94453 11.41 7.94453 11.5958 8.13016C11.7814 8.31578 11.7814 8.61672 11.5956 8.80234Z"
                  fill="white"
                />
              </svg>

              <span className="text-white text-[14px] font-bold uppercase">
                {t('Export {{count}} Profiles to Excel', { count: data.count })}
              </span>
            </button>
            <button
              className="flex items-center justify-center gap-2.5 px-4 py-[11px] bg-[#EC4899] rounded-md hover:bg-[#db2777] transition-colors"
              onClick={() => {
                ga_logEvent(EventName.export_emails_click);
                handleExport(false);
              }}
            >
              <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M8.5 0.34375C4.27156 0.34375 0.84375 3.77156 0.84375 8C0.84375 12.2284 4.27156 15.6562 8.5 15.6562C12.7284 15.6562 16.1562 12.2284 16.1562 8C16.1562 3.77156 12.7284 0.34375 8.5 0.34375ZM11.5956 8.80234L8.87844 11.5197C8.86625 11.5353 8.85328 11.5505 8.83891 11.5647C8.74531 11.6583 8.62266 11.7045 8.5 11.7039C8.3775 11.7047 8.25469 11.6583 8.16109 11.5647C8.14672 11.5503 8.13359 11.5353 8.12156 11.5197L5.40438 8.8025C5.21875 8.61687 5.21875 8.31594 5.40438 8.13031C5.59 7.94469 5.89094 7.94469 6.07672 8.13031L8.02469 10.0781V4.77141C8.02469 4.50891 8.2375 4.29609 8.5 4.29609C8.7625 4.29609 8.97531 4.50891 8.97531 4.77141V10.0781L10.9234 8.13016C11.1091 7.94453 11.41 7.94453 11.5958 8.13016C11.7814 8.31578 11.7814 8.61672 11.5956 8.80234Z"
                  fill="white"
                />
              </svg>

              <span className="text-white text-[14px] font-bold uppercase">
                {t('Export {{count}} emails To excel', { count: data.email_count })}
              </span>
            </button>
          </div>
        </div>
        {/* 表格 */}
        <table className="w-full lg:table hidden">
          <thead>
            <tr className="border-b border-[#E4E4E7]">
              <th className="px-4 py-4 text-left">
                <span className="text-[#71717A] text-[14px] font-semibold">#</span>
              </th>
              <th className="px-4 py-4 text-left">
                <span className="text-[#71717A] text-[14px] font-semibold">{t('User Information')}</span>
              </th>
              <th className="px-4 py-4 text-left">
                <button
                  onClick={() => handleSort('followers')}
                  className="flex items-center gap-2 text-[#71717A] text-[14px] font-semibold hover:text-[#09090B] transition-colors"
                >
                  {t('Followers')}
                  <div className="flex flex-col -space-y-1">
                    <ChevronUp
                      size={14}
                      className={cn(
                        'transition-colors',
                        sortColumn === 'followers' && sortDirection === 'asc'
                          ? 'text-[#725CFF] stroke-[2.5]'
                          : 'text-[#D4D4D8] stroke-[2]'
                      )}
                    />
                    <ChevronDown
                      size={14}
                      className={cn(
                        'transition-colors',
                        sortColumn === 'followers' && sortDirection === 'desc'
                          ? 'text-[#725CFF] stroke-[2.5]'
                          : 'text-[#D4D4D8] stroke-[2]'
                      )}
                    />
                  </div>
                </button>
              </th>
              <th className="px-4 py-4 text-left">
                <button
                  onClick={() => handleSort('following')}
                  className="flex items-center gap-2 text-[#71717A] text-[14px] font-semibold hover:text-[#09090B] transition-colors"
                >
                  {t('Following')}
                  <div className="flex flex-col -space-y-1">
                    <ChevronUp
                      size={14}
                      className={cn(
                        'transition-colors',
                        sortColumn === 'following' && sortDirection === 'asc'
                          ? 'text-[#725CFF] stroke-[2.5]'
                          : 'text-[#D4D4D8] stroke-[2]'
                      )}
                    />
                    <ChevronDown
                      size={14}
                      className={cn(
                        'transition-colors',
                        sortColumn === 'following' && sortDirection === 'desc'
                          ? 'text-[#725CFF] stroke-[2.5]'
                          : 'text-[#D4D4D8] stroke-[2]'
                      )}
                    />
                  </div>
                </button>
              </th>
              <th className="px-4 py-4 text-left">
                <button
                  onClick={() => handleSort('posts')}
                  className="flex items-center gap-2 text-[#71717A] text-[14px] font-semibold hover:text-[#09090B] transition-colors"
                >
                  {t('Posts')}
                  <div className="flex flex-col -space-y-1">
                    <ChevronUp
                      size={14}
                      className={cn(
                        'transition-colors',
                        sortColumn === 'posts' && sortDirection === 'asc'
                          ? 'text-[#725CFF] stroke-[2.5]'
                          : 'text-[#D4D4D8] stroke-[2]'
                      )}
                    />
                    <ChevronDown
                      size={14}
                      className={cn(
                        'transition-colors',
                        sortColumn === 'posts' && sortDirection === 'desc'
                          ? 'text-[#725CFF] stroke-[2.5]'
                          : 'text-[#D4D4D8] stroke-[2]'
                      )}
                    />
                  </div>
                </button>
              </th>
              <th className="px-4 py-4 text-left min-w-[200px]">
                <span className="text-[#71717A] text-[14px] font-semibold">{t('Biography')}</span>
              </th>
              <th className="px-4 py-4 text-left">
                <span className="text-[#71717A] text-[14px] font-semibold">{t('Category')}</span>
              </th>
              <th className="px-4 py-4 text-left">
                <button
                  onClick={() => handleSort('email')}
                  className="flex items-center gap-2 text-[#71717A] text-[14px] font-semibold hover:text-[#09090B] transition-colors"
                >
                  {t('Email')}
                  <div className="flex flex-col -space-y-1">
                    <ChevronUp
                      size={14}
                      className={cn(
                        'transition-colors',
                        sortColumn === 'email' && sortDirection === 'asc'
                          ? 'text-[#725CFF] stroke-[2.5]'
                          : 'text-[#D4D4D8] stroke-[2]'
                      )}
                    />
                    <ChevronDown
                      size={14}
                      className={cn(
                        'transition-colors',
                        sortColumn === 'email' && sortDirection === 'desc'
                          ? 'text-[#725CFF] stroke-[2.5]'
                          : 'text-[#D4D4D8] stroke-[2]'
                      )}
                    />
                  </div>
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {currentData.map((profile: any, index: number) => (
              <tr key={profile.id} className="border-b border-[#E4E4E7] hover:bg-gray-50 transition-colors">
                <td className="px-4 py-4">
                  <span className="text-[#11181A] text-[14px] font-medium">
                    {(currentPage - 1) * rowsPerPage + index + 1}
                  </span>
                </td>
                <td className="px-4 py-2">
                  <div
                    className="flex items-center gap-2 cursor-pointer"
                    onClick={() => {
                      window.open(profile.media_url, '_blank');
                    }}
                  >
                    <div className="size-10 rounded-full flex-shrink-0 overflow-hidden">
                      <LocalImage
                        src={profile.profile_pic_url}
                        className="rounded-full"
                        alt={profile.username || 'avatar'}
                      />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[#11181A] text-[14px] font-medium w-[70px] truncate">
                        @{profile.username || '-'}
                      </span>
                      <span className="text-[#11181A] text-[12px] font-medium opacity-60 w-[70px] truncate">
                        {profile.full_name || '-'}
                      </span>
                      {profile.is_private && (
                        <div className="flex items-center justify-center px-2.5 py-[3px] bg-[#E4E4E7] rounded-[22px] w-fit mt-1">
                          <span className="text-xs font-medium text-[#11181A]/60">{t('Private')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <span className="text-[#11181A] text-[14px] font-medium">
                    {formatNumber(profile.follower_count) || 0}
                  </span>
                </td>
                <td className="px-4 py-4">
                  <span className="text-[#11181A] text-[14px] font-medium">
                    {formatNumber(profile.following_count) || 0}
                  </span>
                </td>
                <td className="px-4 py-4">
                  <span className="text-[#11181A] text-[14px] font-medium">
                    {formatNumber(profile.media_count) || 0}
                  </span>
                </td>
                <td className="px-4 py-4">
                  <div className="text-[#11181A] text-[14px] font-medium line-clamp-2 w-[200px]">{profile.profile}</div>
                </td>
                <td className="px-4 py-4">
                  <span className="text-[#11181A] text-[14px] font-medium">{profile.category || '-'}</span>
                </td>
                <td className="px-4 py-4 ">
                  <div className="flex items-center gap-2 w-[100px]">
                    <span
                      className="text-[14px] font-medium text-wrap break-all"
                      style={{
                        color: profile.available_email
                          ? '#2D9C4D'
                          : profile.available_email === false
                            ? '#FF0C59'
                            : !profile.email
                              ? '#11181A'
                              : '#3F92FF',
                      }}
                    >
                      {profile.email || 'No email'}
                    </span>
                    {profile.available_email === false && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span>{ErrorIcon}</span>
                          </TooltipTrigger>
                          <TooltipContent side="bottom" className="max-w-[200px]">
                            {t('Email verification failed. This address may be invalid or undeliverable.')}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {/* 列表 */}
        <div className="lg:hidden">
          <div className="flex items-center justify-between border-t border-[#E4E4E7] py-4 px-1">
            <div className="w-[40%]">{t('User Information')}</div>
            <div className="w-[30%]">{t('Category')}</div>
            <div className="w-[20%]">{t('Email')}</div>
            <div className="flex flex-col -space-y-1 w-[10%]" onClick={() => handleSort('email')}>
              <ChevronUp
                size={14}
                className={cn(
                  'transition-colors',
                  sortColumn === 'email' && sortDirection === 'asc'
                    ? 'text-[#725CFF] stroke-[2.5]'
                    : 'text-[#D4D4D8] stroke-[2]'
                )}
              />
              <ChevronDown
                size={14}
                className={cn(
                  'transition-colors',
                  sortColumn === 'email' && sortDirection === 'desc'
                    ? 'text-[#725CFF] stroke-[2.5]'
                    : 'text-[#D4D4D8] stroke-[2]'
                )}
              />
            </div>
          </div>
          <ul>
            {currentData.map((profile: any, index: number) => (
              <li key={index} className="border-t border-[#E4E4E7] py-4 px-1">
                <div className="flex items-center gap-4 w-full mb-2">
                  <div
                    className="flex items-center gap-2 w-[40%]"
                    onClick={() => {
                      window.open(profile.media_url, '_blank');
                    }}
                  >
                    <div className="size-10 rounded-full flex-shrink-0 overflow-hidden">
                      <LocalImage
                        src={profile.profile_pic_url}
                        className="rounded-full"
                        alt={profile.full_name || 'avatar'}
                      />
                    </div>
                    <div className="flex flex-col">
                      <div className="text-[#11181A] text-[14px] font-medium flex items-center gap-1">
                        <div className="truncate leading-4 w-[50px] flex-shrink-0">@{profile.username}</div>
                        <svg
                          width="16"
                          className="flex-shrink-0"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M2 14V2H6.72667V3.33333H3.33333V12.6667H12.6667V9.27333H14V14H2ZM7.05733 8L11.724 3.33333H8.66667V2H14V7.33333H12.6667V4.276L8 8.94267L7.05733 8Z"
                            fill="#0C0C0C"
                          />
                        </svg>
                      </div>
                      <span className="text-[#11181A] text-[12px] font-medium opacity-60 line-clamp-2 leading-4">
                        {profile.full_name}
                      </span>
                    </div>
                  </div>
                  <div className="w-[30%] text-wrap text-sm font-semibold break-all">{profile.category || '-'}</div>
                  <div
                    className={`w-[20%] text-wrap break-all text-sm`}
                    style={{
                      color: profile.available_email
                        ? '#2D9C4D'
                        : profile.available_email === false
                          ? '#FF0C59'
                          : !profile.email
                            ? '#11181A'
                            : '#3F92FF',
                    }}
                  >
                    {profile.email || 'No email'}
                  </div>
                  <div className="w-[5%]">
                    {profile.available_email === false && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <span className="cursor-pointer inline-block">{ErrorIcon}</span>
                        </PopoverTrigger>
                        <PopoverContent
                          side="bottom"
                          className="max-w-[200px] w-auto p-3 text-xs bg-gray-900 text-white border-none"
                        >
                          {t('Email verification failed. This address may be invalid or undeliverable.')}
                        </PopoverContent>
                      </Popover>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-10  bg-[#F4F5F9] p-2 rounded-lg text-gray-500 text-sm">
                  <div className="flex-1">
                    <span className="text-black">{formatNumber(profile.follower_count) || 0}</span> {t('followers')}
                  </div>
                  <div className="flex-1">
                    <span className="text-black ">{formatNumber(profile.following_count) || 0}</span> {t('following')}
                  </div>
                  <div className="flex-1">
                    <span className="text-black">{formatNumber(profile.media_count) || 0}</span> {t('posts')}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex lg:flex-row flex-col-reverse items-center justify-center lg:gap-8 gap-4 py-6 border-t border-[#E4E4E7]">
          <div className="flex items-center gap-2">
            <span className="text-[#09090B] text-sm font-medium">{t('Rows per page')}</span>
            <Select
              value={rowsPerPage.toString()}
              onValueChange={(value) => {
                setRowsPerPage(Number(value));
                setCurrentPage(1);
              }}
            >
              <SelectTrigger className="w-[80px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5</SelectItem>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <div className="text-[#09090B] text-sm font-medium">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex items-center gap-2">
              {/* 跳转到第一页 */}
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className={cn(
                  'size-8 flex items-center justify-center rounded-lg border border-[#E4E4E7] transition-all',
                  currentPage === 1
                    ? 'bg-white text-[#D4D4D8] cursor-not-allowed'
                    : 'bg-white text-[#09090B] hover:bg-[#F4F4F5] hover:border-[#A1A1AA]'
                )}
              >
                <ChevronsLeft size={16} />
              </button>

              {/* 上一页 */}
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className={cn(
                  'size-8 flex items-center justify-center rounded-lg border border-[#E4E4E7] transition-all',
                  currentPage === 1
                    ? 'bg-white text-[#D4D4D8] cursor-not-allowed'
                    : 'bg-white text-[#09090B] hover:bg-[#F4F4F5] hover:border-[#A1A1AA]'
                )}
              >
                <ChevronLeft size={16} />
              </button>

              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages || data?.items?.length === 0}
                className={cn(
                  'size-8 flex items-center justify-center rounded-lg border border-[#E4E4E7] transition-all',
                  currentPage === totalPages || data?.items?.length === 0
                    ? 'bg-white text-[#D4D4D8] cursor-not-allowed'
                    : 'bg-white text-[#09090B] hover:bg-[#F4F4F5] hover:border-[#A1A1AA]'
                )}
              >
                <ChevronRight size={16} />
              </button>

              {/* 跳转到最后一页 */}
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages || data?.items?.length === 0}
                className={cn(
                  'size-8 flex items-center justify-center rounded-lg border border-[#E4E4E7] transition-all',
                  currentPage === totalPages || data?.items?.length === 0
                    ? 'bg-white text-[#D4D4D8] cursor-not-allowed'
                    : 'bg-white text-[#09090B] hover:bg-[#F4F4F5] hover:border-[#A1A1AA]'
                )}
              >
                <ChevronsRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
